import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import apiClient, { formatApiError, authedFileUrl } from "@/api/client";
import { ArrowLeft, Send, Sparkles, EyeOff, FileText, Download } from "lucide-react";
import Avatar from "@/components/Avatar";

export default function TicketDetailPage() {
  const { tid } = useParams(); const nav = useNavigate();
  const [data, setData] = useState(null); const [team, setTeam] = useState([]);
  const [reply, setReply] = useState(""); const [internal, setInternal] = useState(false);

  const load = () => apiClient.get(`/tickets/${tid}`).then(r => setData(r.data));
  useEffect(() => { load(); apiClient.get("/team").then(r => setTeam(r.data)); }, [tid]);

  const send = async () => {
    if (!reply.trim()) return;
    await apiClient.post(`/tickets/${tid}/reply`, { body: reply, internal });
    setReply(""); load();
  };
  const useAI = () => setReply(data?.ticket?.ai_suggested_reply || "");
  const assign = async (assignee_id) => { await apiClient.post(`/tickets/${tid}/assign`, { assignee_id }); load(); };
  const setStatus = async (status) => { await apiClient.post(`/tickets/${tid}/status`, { status }); load(); };

  if (!data) return <div className="text-sm text-[#94A3B8]">Yükleniyor…</div>;
  const t = data.ticket;

  return (
    <div className="space-y-5" data-testid="ticket-detail-page">
      <button onClick={() => nav("/destek")} className="ky-btn ky-btn-ghost ky-btn-sm"><ArrowLeft size={12}/>Destek Talepleri</button>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="ky-card p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div><h1 className="text-xl font-bold">{t.subject}</h1><div className="text-xs text-[#94A3B8] mt-1">Müşteri: <Link to={`/musteriler/${t.customer_id}`} className="ky-link">{t.customer_name}</Link> · Açılma: {new Date(t.created_at).toLocaleString("tr-TR")}</div></div>
              <div className="flex gap-2">
                <select value={t.status} onChange={e => setStatus(e.target.value)} className="ky-select h-8 text-xs w-28" data-testid="ticket-status"><option value="open">Açık</option><option value="pending">Bekliyor</option><option value="closed">Kapalı</option></select>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {data.messages.map(m => (
              <div key={m.id} className={`flex gap-3 ${m.author_type === "team" ? "flex-row-reverse" : ""}`}>
                <Avatar name={m.author_name} size={32}/>
                <div className={`max-w-[80%] ky-card p-4 ${m.author_type === "team" ? "bg-[#EEF2FF] border-[#C7D2FE]" : ""} ${m.internal ? "border-[#FDE68A] bg-[#FFFBEB]" : ""}`}>
                  <div className="flex items-center gap-2 mb-1.5"><span className="text-xs font-semibold">{m.author_name}</span>{m.internal && <span className="ky-badge ky-badge-warning"><EyeOff size={9}/>İç not</span>}<span className="text-[10px] text-[#94A3B8]">{new Date(m.created_at).toLocaleString("tr-TR")}</span></div>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
                </div>
              </div>
            ))}
          </div>

          {t.ai_suggested_reply && (
            <div className="ky-card p-4 border-[#C7D2FE] bg-[#EEF2FF]/40" data-testid="ai-suggested-reply">
              <div className="flex items-center gap-2 mb-2"><Sparkles size={14} className="text-[#4F46E5]"/><div className="text-sm font-semibold">AI Önerilen Yanıt</div><button onClick={useAI} className="ml-auto ky-btn ky-btn-outline ky-btn-sm">Kullan</button></div>
              <div className="text-sm text-[#0F172A] leading-relaxed whitespace-pre-wrap">{t.ai_suggested_reply}</div>
            </div>
          )}

          <div className="ky-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Yanıt</div>
              <label className="flex items-center gap-1.5 text-xs text-[#64748B]"><input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} data-testid="reply-internal"/>İç not (müşteri görmez)</label>
            </div>
            <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Yanıtınızı yazın…" className="ky-textarea" rows={5} data-testid="ticket-reply-input"/>
            <div className="mt-3 flex justify-end"><button onClick={send} className="ky-btn ky-btn-accent" data-testid="ticket-send"><Send size={13}/>Gönder</button></div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="ky-card p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-3">Atanan</div>
            <select value={t.assignee_id || ""} onChange={e => assign(e.target.value || null)} className="ky-select"><option value="">— Atanmadı —</option>{team.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
          </div>
          {data.files.length > 0 && (
            <div className="ky-card p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-3">Ekler ({data.files.length})</div>
              <div className="space-y-2">{data.files.map(f => <a key={f.id} href={authedFileUrl(f.id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm hover:bg-[#FAFAFB] p-2 rounded-md"><FileText size={14}/>{f.original_filename}<Download size={11} className="ml-auto text-[#94A3B8]"/></a>)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
